import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { EnterIcon, ImageIcon, Pencil1Icon, PersonIcon, TrashIcon } from '@radix-ui/react-icons';
import { Card, Person } from '@producer/ui-kit';

import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { updateCharacterCover } from '../character.slice';
import { CoverImage } from '../../../components/cover-image/cover-image';
import { catchError } from '../../../core/catch-error';
import { FormBuilderModal } from '../../../components/form-builder-modal/form-builder-modal';
import { buildCharacterForm } from '../character-form.definition';
import { formatPicture } from '../../../core/services/image.service';
import { Project } from '../../../app/entities/project';
import { COVER_ACCEPT, COVER_SIZE } from '../../../components/cover-image/cover-format';
import { selectFiles } from '../../../core/services/file.service';

import type { Character } from '../../../app/entities/character';
import type { Frame } from '../../../lib/utils/transform-image';
import { openModal } from '../../../core/modal/open-modal';

type CharacterProps = {
  character: Character;
};

export const CharacterItem: React.FC<CharacterProps> = observer(({ character }) => {
  const { name } = character;
  const navigate = useNavigate();
  const location = useLocation();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updateModalKey, setUpdateModalKey] = useState(character?._id);

  const isCharactersRoute = location.pathname.includes('/characters'.toLowerCase());

  const handleCharacterNavigation = () => {
    navigate(`./${character?._id}`);
  };

  const handleCandidatesNavigation = () => {
    if (!isCharactersRoute) {
      navigate(`./${character?._id}`);
      return;
    }

    const projectId = character.projectId;
    const project = Project.getOne(projectId);
    if (project) {
      const castingStep = project.steps.find((el) => el.type === 'casting');
      if (castingStep) {
        navigate(`../steps/${castingStep._id}/${character?._id}`);
      }
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await character.delete();
    } catch (e) {
      catchError(e);
    }
  };

  const handleEditCover = async (file: Frame) => {
    try {
      await updateCharacterCover({ characterId: character._id, file });
    } catch (e) {
      catchError(e);
    }
  };

  const handleEdit = async (
    data: Omit<Partial<Character>, 'characterNumber'> & { characterNumber?: number | string },
  ) => {
    const { characterNumber, ...rest } = data;
    const parsed =
      characterNumber === '' || characterNumber === undefined ? undefined : Number(characterNumber);

    await character.update(parsed === undefined ? rest : { ...rest, characterNumber: parsed });
    // To rerender the update form modal
    setUpdateModalKey(character._id + Math.random());
  };

  const handleUploadPhoto = async (file?: File) => {
    if (!file) {
      return;
    }
    const frame = await formatPicture(file, COVER_SIZE);
    await updateCharacterCover({ characterId: character._id, file: frame });
  };

  /** The way in for changing a cover: the empty state is inert, so the menu owns it. */
  const handleEditCoverFromMenu = async () => {
    const files = await selectFiles({ accept: COVER_ACCEPT });

    if (!files?.length) {
      return;
    }

    await handleUploadPhoto(files[0]);
  };

  const menuItems = [
    {
      onSelect: handleCharacterNavigation,
      title: 'Open',
      icon: <EnterIcon />,
    },
    {
      onSelect: () => setIsEditModalOpen(true),
      title: 'Edit',
      icon: <Pencil1Icon />,
    },
    {
      onSelect: handleCandidatesNavigation,
      title: 'Manage candidates',
      icon: <PersonIcon />,
    },
    {
      title: 'Edit cover\u2026',
      icon: <ImageIcon />,
      onSelect: handleEditCoverFromMenu,
    },
    { separator: true as const },
    {
      onSelect: () =>
        openModal(DeleteDialog, {
          onSubmit: handleConfirmDelete,
          title: 'Delete character',
          text: `Are you sure you want to delete ${name} ?`,
        }),
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
    },
  ];

  const siblings = (Project.getOne(character.projectId)?.characters ?? []).filter(
    (sibling) => sibling._id !== character._id,
  );

  const updateCharacterFormDefinition = buildCharacterForm(false, handleUploadPhoto, siblings);

  return (
    <>
      <Card
        media={
          <CoverImage
            src={character.cover?.src || ''}
            title={character.name}
            onEdit={handleEditCover}
            placeholderIcon={<Person />}
          />
        }
        badge={character.characterNumber}
        title={name}
        subtitle={character.castName || undefined}
        menu={menuItems}
        onClick={handleCharacterNavigation}
      />

      {isEditModalOpen && (
        <FormBuilderModal
          key={updateModalKey}
          model="character"
          definition={updateCharacterFormDefinition}
          initialValues={{ ...character, avatar: character.cover?.src }}
          onSubmit={handleEdit}
          onCancel={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  );
});
